import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowLeft, FiLogOut, FiMenu, FiX, FiChevronRight,
  FiGrid, FiShoppingBag, FiBox, FiTag, FiUsers, FiShield, FiSlash, FiBarChart2, FiStar,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const MOBILE = '1100px';
const DESKTOP = '1101px';

const AdminNav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 50;
  background:
    linear-gradient(180deg, var(--adm-sage-deep) 0%, #333d2b 100%);
  color: #f4f1e9;
  border-bottom: 1px solid rgba(176, 141, 87, .35);
  box-shadow: 0 10px 30px -20px rgba(0, 0, 0, .8);
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  min-height: 72px;

  @media (max-width: ${MOBILE}) {
    padding: 0 1.25rem;
    min-height: 64px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  flex-shrink: 0;
  line-height: 1;
`;

const BrandMark = styled.span`
  font-family: 'Goudy Bookletter 1911', serif;
  font-size: 1.55rem;
  color: #fff;
  letter-spacing: .02em;
`;

const BrandSub = styled.span`
  margin-top: 4px;
  font-size: .58rem;
  letter-spacing: .42em;
  text-transform: uppercase;
  color: var(--adm-brass);
`;

/* desktop horizontal links */
const NavLinks = styled.div`
  display: flex;
  gap: .25rem;
  align-items: center;
  flex: 1;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE}) { display: none; }
`;

const NavLink = styled(Link)`
  position: relative;
  color: ${props => (props.$active ? '#fff' : 'rgba(244, 241, 233, .72)')};
  text-decoration: none;
  padding: .55rem .85rem;
  font-size: .82rem;
  letter-spacing: .03em;
  border-radius: 3px;
  transition: color .2s ease, background .2s ease;

  &::after {
    content: '';
    position: absolute;
    left: .85rem;
    right: .85rem;
    bottom: .28rem;
    height: 1.5px;
    background: var(--adm-brass);
    transform: scaleX(${props => (props.$active ? 1 : 0)});
    transform-origin: left;
    transition: transform .25s ease;
  }

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, .06);
  }
  &:hover::after { transform: scaleX(1); }
`;

/* desktop user section */
const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;

  @media (max-width: ${MOBILE}) { display: none; }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 1rem;
  border-right: 1px solid rgba(244, 241, 233, .18);
  min-width: 0;
  max-width: 200px;
`;

const UserName = styled.div`
  font-weight: 500;
  font-size: .85rem;
  color: #fff;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: .62rem;
  letter-spacing: .22em;
  text-transform: uppercase;
  color: var(--adm-brass);
  margin-top: 2px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  background: transparent;
  color: rgba(244, 241, 233, .82);
  border: 1px solid rgba(244, 241, 233, .22);
  padding: .5rem .9rem;
  border-radius: 2px;
  cursor: pointer;
  font-family: inherit;
  font-size: .72rem;
  letter-spacing: .12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: all .2s ease;

  svg { width: 14px; height: 14px; }

  &:hover {
    color: #fff;
    border-color: ${props => (props.$danger ? 'var(--adm-clay)' : 'var(--adm-brass)')};
    background: ${props => (props.$danger ? 'rgba(181, 101, 74, .22)' : 'rgba(176, 141, 87, .16)')};
  }
`;

/* hamburger toggle — mobile only */
const Hamburger = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: transparent;
  color: #f4f1e9;
  border: 1px solid rgba(244, 241, 233, .25);
  border-radius: 3px;
  cursor: pointer;

  svg { width: 22px; height: 22px; }

  @media (max-width: ${MOBILE}) { display: inline-flex; }
`;

/* ---- mobile slide-in drawer ------------------------------------------- */
const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(18, 22, 14, .55);
  opacity: ${props => (props.$open ? 1 : 0)};
  visibility: ${props => (props.$open ? 'visible' : 'hidden')};
  transition: opacity .25s ease, visibility .25s ease;
  z-index: 1200;

  @media (min-width: ${DESKTOP}) { display: none; }
`;

const MobileDrawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(88vw, 380px);
  background: linear-gradient(180deg, var(--adm-sage-deep) 0%, #2f3726 100%);
  color: #f4f1e9;
  box-shadow: ${props => (props.$open ? '-12px 0 48px rgba(0, 0, 0, .5)' : 'none')};
  transform: translateX(${props => (props.$open ? '0' : '100%')});
  transition: transform .3s cubic-bezier(.4, 0, .2, 1);
  z-index: 1201;
  display: flex;
  flex-direction: column;

  @media (min-width: ${DESKTOP}) { display: none; }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid rgba(176, 141, 87, .3);
  flex-shrink: 0;
`;

const DrawerBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1;
`;

const DrawerClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: transparent;
  color: #f4f1e9;
  border: 1px solid rgba(244, 241, 233, .25);
  border-radius: 3px;
  cursor: pointer;
  transition: all .2s ease;

  svg { width: 22px; height: 22px; }
  &:hover { border-color: var(--adm-brass); background: rgba(176, 141, 87, .16); }
`;

const DrawerBody = styled.nav`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: .35rem 0;
`;

const DrawerLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  padding: .9rem 1.25rem;
  min-height: 52px;
  color: ${props => (props.$active ? '#fff' : 'rgba(244, 241, 233, .78)')};
  background: ${props => (props.$active ? 'rgba(176, 141, 87, .14)' : 'transparent')};
  border-left: 3px solid ${props => (props.$active ? 'var(--adm-brass)' : 'transparent')};
  border-bottom: 1px solid rgba(244, 241, 233, .07);
  text-decoration: none;
  font-size: 1rem;
  letter-spacing: .01em;
  transition: background .18s ease, color .18s ease;

  .label { display: inline-flex; align-items: center; gap: .9rem; min-width: 0; }
  .label svg { width: 18px; height: 18px; flex-shrink: 0; color: ${props => (props.$active ? 'var(--adm-brass)' : 'rgba(244, 241, 233, .5)')}; }
  .chevron { width: 17px; height: 17px; flex-shrink: 0; color: rgba(244, 241, 233, .3); }

  &:hover, &:active {
    color: #fff;
    background: rgba(255, 255, 255, .05);
    .label svg { color: var(--adm-brass); }
  }
`;

const DrawerUser = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 1.25rem 1.25rem 1rem;
  border-top: 1px solid rgba(244, 241, 233, .08);
  margin-top: .25rem;
`;

const DrawerUserName = styled.div`
  font-weight: 500;
  font-size: 1.05rem;
  color: #fff;
  word-break: break-word;
`;

const DrawerFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: .75rem;
  padding: 1rem 1.25rem calc(1rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(176, 141, 87, .25);

  ${IconButton} { flex: 1; padding: .85rem; }
`;

const AdminNavbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // close the drawer on navigation
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // close on Escape + lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const fullName = `${user?.firstName || ''}${user?.lastName ? ` ${user.lastName}` : ''}`.trim();

  const links = [
    { to: '/admin', label: 'Gösterge Paneli', icon: FiGrid, active: location.pathname === '/admin' },
    { to: '/admin/orders', label: 'Siparişler', icon: FiShoppingBag, active: isActive('/admin/orders') },
    { to: '/admin/products', label: 'Ürünler', icon: FiBox, active: isActive('/admin/products') },
    { to: '/admin/reviews', label: 'Yorumlar', icon: FiStar, active: isActive('/admin/reviews') },
    { to: '/admin/campaigns', label: 'Kampanyalar', icon: FiTag, active: isActive('/admin/campaigns') },
    { to: '/admin/customers', label: 'Müşteriler', icon: FiUsers, active: isActive('/admin/customers') },
    { to: '/admin/admin-users', label: 'Yöneticiler', icon: FiShield, active: isActive('/admin/admin-users') },
    { to: '/admin/blocklist', label: 'Blocklist', icon: FiSlash, active: isActive('/admin/blocklist') },
    { to: '/admin/analytics', label: 'Analitik', icon: FiBarChart2, active: isActive('/admin/analytics') },
  ];

  return (
    <AdminNav>
      <NavContainer>
        <Brand to="/admin">
          <BrandMark>moria</BrandMark>
          <BrandSub>Yönetici Portalı</BrandSub>
        </Brand>

        <NavLinks>
          {links.map(link => (
            <NavLink key={link.to} to={link.to} $active={link.active}>
              {link.label}
            </NavLink>
          ))}
        </NavLinks>

        <UserSection>
          <UserInfo>
            <UserName title={fullName}>{fullName}</UserName>
            <UserRole>Yönetici</UserRole>
          </UserInfo>
          <IconButton as={Link} to="/">
            <FiArrowLeft /> Siteye Dön
          </IconButton>
          <IconButton $danger onClick={handleLogout}>
            <FiLogOut /> Çıkış
          </IconButton>
        </UserSection>

        <Hamburger
          onClick={() => setOpen(o => !o)}
          aria-label="Menüyü aç"
          aria-expanded={open}
        >
          <FiMenu />
        </Hamburger>
      </NavContainer>

      {/* Mobile slide-in drawer — portaled to body so it overlays the
          storefront navbar and escapes any ancestor stacking context */}
      {createPortal(
        <>
          <MobileOverlay $open={open} onClick={() => setOpen(false)} aria-hidden="true" />
          <MobileDrawer $open={open} role="dialog" aria-modal="true" aria-label="Yönetici menüsü">
            <DrawerHeader>
              <DrawerBrand>
                <BrandMark>moria</BrandMark>
                <BrandSub>Yönetici Portalı</BrandSub>
              </DrawerBrand>
              <DrawerClose onClick={() => setOpen(false)} aria-label="Menüyü kapat">
                <FiX />
              </DrawerClose>
            </DrawerHeader>

            <DrawerBody>
              {links.map(({ to, label, icon: Icon, active }) => (
                <DrawerLink key={to} to={to} $active={active} onClick={() => setOpen(false)}>
                  <span className="label"><Icon />{label}</span>
                  <FiChevronRight className="chevron" />
                </DrawerLink>
              ))}
              <DrawerUser>
                <DrawerUserName>{fullName}</DrawerUserName>
                <UserRole>Yönetici</UserRole>
              </DrawerUser>
            </DrawerBody>

            <DrawerFooter>
              <IconButton as={Link} to="/" onClick={() => setOpen(false)}>
                <FiArrowLeft /> Siteye Dön
              </IconButton>
              <IconButton $danger onClick={handleLogout}>
                <FiLogOut /> Çıkış
              </IconButton>
            </DrawerFooter>
          </MobileDrawer>
        </>,
        document.body
      )}
    </AdminNav>
  );
};

export default AdminNavbar;
